import CharacterCount from '@tiptap/extension-character-count';
import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Bold, Italic, List, ListOrdered, Redo, Undo } from 'lucide-react';

interface LetterEditorProps {
  content?: string;
  onChange?: (json: unknown) => void;
  placeholder?: string;
  maxLength?: number;
  editable?: boolean;
  className?: string;
  borderless?: boolean;
  fontFamily?: string;
}

export function LetterEditor({
  content,
  onChange,
  placeholder = 'Write your letter...',
  maxLength = 10000,
  editable = true,
  className = '',
  borderless = false,
  fontFamily
}: LetterEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        code: false,
        horizontalRule: false
      }),
      Placeholder.configure({
        placeholder
      }),
      CharacterCount.configure({
        limit: maxLength
      })
    ],
    content: content ? JSON.parse(content) : undefined,
    editable,
    immediatelyRender: false,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getJSON());
    },
    editorProps: {
      attributes: {
        class: 'letter-content prose prose-lg max-w-none focus:outline-none min-h-[300px] p-4',
        style: fontFamily ? `font-family: ${fontFamily}` : ''
      }
    }
  });

  if (!editor) {
    return null;
  }

  const characterCount = editor.storage.characterCount.characters();
  const characterPercentage = Math.round((characterCount / maxLength) * 100);

  return (
    <div
      className={`${borderless ? '' : 'border border-gray-warm rounded-lg overflow-hidden bg-white'} ${className}`}
    >
      {/* Toolbar */}
      {editable && (
        <div
          className={`flex items-center gap-1 p-2 ${borderless ? 'border-b border-gray-warm/30 mb-4' : 'border-b border-gray-warm/50 bg-cream/50'}`}
        >
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            active={editor.isActive('bold')}
            title="Bold"
          >
            <Bold className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            active={editor.isActive('italic')}
            title="Italic"
          >
            <Italic className="w-4 h-4" />
          </ToolbarButton>

          <div className="w-px h-5 bg-gray-warm/50 mx-1" />

          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            active={editor.isActive('bulletList')}
            title="Bullet List"
          >
            <List className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            active={editor.isActive('orderedList')}
            title="Numbered List"
          >
            <ListOrdered className="w-4 h-4" />
          </ToolbarButton>

          <div className="w-px h-5 bg-gray-warm/50 mx-1" />

          <ToolbarButton
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            title="Undo"
          >
            <Undo className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            title="Redo"
          >
            <Redo className="w-4 h-4" />
          </ToolbarButton>

          <div className="flex-1" />

          {/* Character count */}
          <span className={`text-xs ${characterPercentage > 90 ? 'text-red' : 'text-sepia/50'}`}>
            {characterCount.toLocaleString()} / {maxLength.toLocaleString()}
          </span>
        </div>
      )}

      {/* Editor */}
      <EditorContent editor={editor} />
    </div>
  );
}

function ToolbarButton({
  onClick,
  active,
  disabled,
  title,
  children
}: {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`p-2 rounded transition-all ${
        active
          ? 'bg-ink text-cream'
          : disabled
            ? 'text-gray-warm cursor-not-allowed'
            : 'text-sepia hover:bg-gray-warm/30'
      }`}
    >
      {children}
    </button>
  );
}

// Read-only letter content display
export function LetterContent({ content, fontFamily }: { content: unknown; fontFamily?: string }) {
  const editor = useEditor({
    extensions: [StarterKit],
    content: content as Parameters<typeof useEditor>[0] extends { content?: infer C } ? C : never,
    editable: false,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: 'letter-content prose prose-lg max-w-none',
        style: fontFamily ? `font-family: ${fontFamily}` : ''
      }
    }
  });

  if (!editor) {
    return null;
  }

  return <EditorContent editor={editor} />;
}
